<!-- eslint-disable vuejs-accessibility/label-has-for -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<script>
import * as d3 from 'd3';
import throttle from 'lodash.throttle';
import { computed, ref } from 'vue';

import ScreenshotDialog from '@/src/components/ScreenshotDialog.vue';
import { useAnalysisStore } from '@/src/store/analysis';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { useImageClipboardStore } from '@/src/store/image-clipboard-store';

import AnalysisGraphLegend from './AnalysisGraphLegend.vue';

export default {
  components: {
    AnalysisGraphLegend,
    ScreenshotDialog,
  },
  props: {
    width: {
      type: Number,
      default: 800,
    },
    height: {
      type: Number,
      default: 230,
    },

    yAxisLabel: {
      type: String,
      default: 'Normalized SI (to LV-BP)',
    },
    viewerID: { type: String, required: true },

    targetMarkers: { required: true },

    columnID: { type: Number },
    imageIndex: { type: Number, required: true },
    smoothCurves: { type: Boolean, default: false },
  },
  emits: ['updateIndex'],

  setup(props, { emit }) {
    const analysisStore = useAnalysisStore();

    const margin = { top: 20, right: 38, bottom: 45, left: 45 };
    const xScale = ref(null);
    const yScale = ref(null);

    // ScrollBar:
    const showBar = ref(false);
    const scrollerWidth = ref(null);
    const scrollerX = ref(0);
    const dragging = ref(false);
    const startDragX = ref(0);
    const startScrollerX = ref(0);
    const maxDrag = ref(null);
    const normalizedData = ref(null);

    const legendColors = {
      Global: '#ff6700',
      LAD: '#ef00af',
      RCA: '#0066ff',
      LCx: '#00bb44',
      LVBPSI: '#0066ff',
      LVBPAREA: '#ff6700',
    };

    const getMarkerColor = marker => legendColors[marker] || '#ff6700';
    const getTerritoryColor = territory =>
      legendColors[territory] || '#ff6700';

    const customDomain = ref(null);
    // const customYDomain = ref(null);

    const showLegend = ref(true);
    const hoveredMarker = ref(null);

    const showTooltip = ref(false);
    const tooltipX = ref(0);
    const tooltipY = ref(0);
    const currentX = ref(0);
    const currentY = ref(0);
    const snappedPointX = ref(0);
    const snappedPointY = ref(0);
    const snappedPointColor = ref('#ff6700');

    // Measurement
    const showMeasurement = ref(false);
    const measurementX = ref(0);
    const measurementY = ref(0);
    const measurementType = ref('x'); // 'x', 'y'
    const deltaX = ref(0);
    const deltaY = ref(0);

    // Brush mode: 'zoom', 'measure'
    const brushMode = ref('zoom');

    // Screenshot state
    const screenshotDialog = ref(false);
    const screenshotDataUrl = ref('');
    const screenshotDimensions = ref({ width: 0, height: 0 });
    const imageClipboardStore = useImageClipboardStore();

    function hideLegend() {
      showLegend.value = false;
    }

    function setVisibleLegend() {
      showLegend.value = true;
    }

    const biomarkerContainer = computed(() =>
      analysisStore.getBiomarkerContainer(),
    );

    const viewerIndx = computed(() => {
      const viewportStateStore = useViewportStateStore();
      return viewportStateStore.getSelectedImageIndices(props.viewerID);
    });

    // Flatten targetMarkers object into array of {biomarker, territory} entries
    const markerEntries = computed(() => {
      const entries = [];
      if (!props.targetMarkers || typeof props.targetMarkers !== 'object')
        return entries;
      Object.keys(props.targetMarkers).forEach((biomarker) => {
        const territories = props.targetMarkers[biomarker] || [];
        const availTerritories
          = biomarkerContainer.value?.getCurveTerritoriesByBiomarkerType(
            biomarker,
          ) || [];

        const sortedTerritories = [...territories].sort((a, b) => {
          const idxA = availTerritories.indexOf(a);
          const idxB = availTerritories.indexOf(b);
          if (idxA !== -1 && idxB !== -1) return idxA - idxB;
          if (idxA !== -1) return -1;
          if (idxB !== -1) return 1;
          return 0;
        });

        sortedTerritories.forEach((territory) => {
          entries.push({ biomarker, territory });
        });
      });
      return entries;
    });

    // Used by the legend - list of territory names (or biomarker if no territory)
    const legendEntries = computed(() =>
      markerEntries.value.map(e => e.territory),
    );

    function getData(biomarker, territory) {
      const currentStudyStore = useCurrentStudyStore();

      const seriesUID = currentStudyStore.getSeriesUID(
        viewerIndx.value.seriesIdx,
      );
      const bioCurve
        = biomarkerContainer.value?.getBiomarkerCurveBySliceIndexTypeAndTerritory(
          seriesUID,
          viewerIndx.value.sliceIdx,
          biomarker,
          territory,
        );

      if (!bioCurve) return { data: [], interpolatedIndexes: [] };

      const data = props.smoothCurves
        ? bioCurve.getSmoothData('svGol')
        : bioCurve.getData();

      return {
        data,
        interpolatedIndexes: bioCurve?.getInterpolatedIndexes?.() || [],
      };
    }

    function getPhaseIndex() {
      const currentStudyStore = useCurrentStudyStore();

      const seriesUID = currentStudyStore.getSeriesUID(
        viewerIndx.value.seriesIdx,
      );

      return biomarkerContainer.value?.getSalientIndexBySliceIndex(
        seriesUID,
        viewerIndx.value.sliceIdx,
      );
    }

    const gradientID = computed(() => `gradient-${props.columnID}`);

    function formatData(yValues) {
      return yValues.map((y, x) => ({ x, y }));
    }

    function findMinMax(data) {
      let min = Infinity;
      let max = -Infinity;

      data.forEach((value) => {
        if (value < min) min = value;
        if (value > max) max = value;
      });

      return [min, max];
    }

    function normalizeToZero(data) {
      // Find the minimum value in the 2D array
      let min = Infinity;

      data.forEach((value) => {
        if (value < min) min = value;
      });

      // Normalize the data by subtracting the minimum value from each element
      return data.map(value => value - min);
    }

    function median(values) {
      values.sort((a, b) => a - b);
      const middle = Math.floor(values.length / 2);

      if (values.length % 2 === 0) {
        return (values[middle - 1] + values[middle]) / 2;
      }
      return values[middle];
    }

    function medianOfColumns(data) {
      const numRows = data.length;
      const numCols = data[0].length;
      const medians = [];

      for (let col = 0; col < numCols; col++) {
        const columnValues = [];

        for (let row = 0; row < numRows; row++) {
          columnValues.push(data[row][col]);
        }

        medians.push(median(columnValues));
      }

      return medians;
    }

    function combineArrays(arr1, arr2) {
      const numRows1 = arr1.length;
      const numRows2 = arr2.length;
      const numCols1 = arr1[0].length;
      const numCols2 = arr2[0].length;

      if (numCols1 !== numCols2) {
        throw new Error('Both arrays must have the same number of columns');
      }

      const combinedArray = [];

      for (let i = 0; i < numRows1; i++) {
        combinedArray.push(arr1[i]);
      }

      for (let i = 0; i < numRows2; i++) {
        combinedArray.push(arr2[i]);
      }

      return combinedArray;
    }

    function findClosestDataPoint(mouseX, mouseY) {
      if (!xScale.value || !yScale.value || !markerEntries.value.length) {
        return null;
      }

      const innerX = mouseX - margin.left;
      const innerY = mouseY - margin.top;

      if (
        innerX < 0
        || innerX > xScale.value.range()[1]
        || innerY < 0
        || innerY > yScale.value.range()[0]
      ) {
        return null;
      }

      let closestPoint = null;
      let minDistanceSquared = Infinity;

      for (
        let entryIndex = 0;
        entryIndex < markerEntries.value.length;
        entryIndex++
      ) {
        const { biomarker, territory } = markerEntries.value[entryIndex];
        const { data } = getData(biomarker, territory);

        for (let pointIndex = 0; pointIndex < data.length; pointIndex++) {
          const pointValue = data[pointIndex];
          const pointX = xScale.value(pointIndex);
          const pointY = yScale.value(pointValue);

          const dx = innerX - pointX;
          const dy = innerY - pointY;
          const distanceSquared = dx * dx + dy * dy;

          if (distanceSquared < minDistanceSquared) {
            minDistanceSquared = distanceSquared;
            closestPoint = {
              x: pointIndex,
              y: pointValue,
              marker: territory,
              color: getTerritoryColor(territory),
              screenX: pointX + margin.left,
              screenY: pointY + margin.top,
            };
          }
        }
      }

      return closestPoint;
    }

    function handleMouseMove(event) {
      if (showMeasurement.value) {
        showTooltip.value = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const closestPoint = findClosestDataPoint(mouseX, mouseY);

      if (!closestPoint) {
        showTooltip.value = false;
        return;
      }

      currentX.value = closestPoint.x;
      currentY.value = closestPoint.y;
      snappedPointX.value = closestPoint.screenX;
      snappedPointY.value = closestPoint.screenY;
      snappedPointColor.value = closestPoint.color;

      tooltipX.value = closestPoint.screenX + 16;
      tooltipY.value = closestPoint.screenY + 14;

      showTooltip.value = true;
    }

    function handleGraphClick(event) {
      if (showMeasurement.value) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      const closestPoint = findClosestDataPoint(mouseX, mouseY);

      if (!closestPoint) {
        return;
      }

      const closestIndex = Math.max(0, Math.round(closestPoint.x));
      emit('updateIndex', closestIndex);
    }

    function hideTooltip() {
      showTooltip.value = false;
    }

    function formatCoordinate(value, precision = 2) {
      return value.toFixed(precision);
    }

    return {
      // ocgCurvesbySlice,
      getData,
      formatData,
      findMinMax,
      normalizeToZero,
      combineArrays,
      medianOfColumns,
      getPhaseIndex,
      // phaseIndexes,
      gradientID,
      viewerIndx,

      // Marker entries:
      markerEntries,
      legendEntries,

      // Legend:
      hideLegend,
      setVisibleLegend,
      showLegend,
      hoveredMarker,

      // Chart:
      analysisStore,
      margin,
      xScale,
      yScale,
      showBar,
      scrollerWidth,
      scrollerX,
      dragging,
      startDragX,
      startScrollerX,
      maxDrag,
      customDomain,
      normalizedData,
      legendColors,
      getMarkerColor,
      getTerritoryColor,

      // Tooltip:
      showTooltip,
      tooltipX,
      tooltipY,
      currentX,
      currentY,
      snappedPointX,
      snappedPointY,
      snappedPointColor,
      findClosestDataPoint,
      handleMouseMove,
      handleGraphClick,
      hideTooltip,
      formatCoordinate,

      // Measurement:
      showMeasurement,
      measurementX,
      measurementY,
      measurementType,
      deltaX,
      deltaY,

      // Brush mode:
      brushMode,
      // setBrushMode,

      // Screenshot
      screenshotDialog,
      screenshotDataUrl,
      handleScreenshotSave(screenshotData) {
        const clipboardImage = {
          imageDataURL: screenshotData.imageDataURL,
          title: screenshotData.title,
          description: screenshotData.description,
          tags: screenshotData.tags,
          viewportId: 'Analysis-Graph',
          seriesIdx: viewerIndx.value.seriesIdx,
          sliceIdx: viewerIndx.value.sliceIdx,
          imageIdx: props.imageIndex,
          width: screenshotDimensions.value.width,
          height: screenshotDimensions.value.height,
          colorbarInfo: screenshotData.colorbarInfo,
        };
        imageClipboardStore.addImage(clipboardImage);
      },
      handleScreenshotDiscard() {
        screenshotDataUrl.value = '';
      },
      screenshotDimensions,
    };
  },
  watch: {
    'viewerIndx.sliceIdx': 'drawChart',
    'viewerIndx.seriesIdx': 'drawChart',
    'phaseIndexes': { handler: 'drawChart', deep: true },
    'smoothCurves': 'drawChart',

    'width': 'drawChart',

    'targetMarkers': { handler: 'drawChart', deep: true },
    'imageIndex': 'drawImageIndexLine',
  },
  mounted() {
    if (this.width < 0) {
      return;
    }
    this.drawChart();
    this.throttledSliderMove = throttle(this.moveScale, 30);
  },
  methods: {
    setBrushMode(mode) {
      this.brushMode = mode;
      this.resetDomain();
    },
    async takeScreenshot() {
      this.resetDomain();
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 1200;
        canvas.height = 700;
        const ctx = canvas.getContext('2d');

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const margin = { top: 20, right: 30, bottom: 100, left: 105 };
        const width = canvas.width - margin.left - margin.right;
        const height = canvas.height - margin.top - margin.bottom;

        const xScale = d3
          .scaleLinear()
          .domain(this.xScale.domain())
          .range([0, width]);

        const yScale = d3
          .scaleLinear()
          .domain(this.yScale.domain())
          .range([height, 0]);

        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(margin.left, canvas.height - margin.bottom);
        ctx.lineTo(canvas.width - margin.right, canvas.height - margin.bottom);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, canvas.height - margin.bottom);
        ctx.stroke();

        const xTicks = xScale.ticks(5);
        ctx.fillStyle = 'black';
        ctx.font = '32px Arial';
        ctx.textAlign = 'center';

        xTicks.forEach((tick) => {
          const x = margin.left + xScale(tick);
          const y = canvas.height - margin.bottom;

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + 5);
          ctx.stroke();

          ctx.fillText(tick.toString(), x, y + 32);
        });

        const yTicks = yScale.ticks(5);
        ctx.textAlign = 'right';

        yTicks.forEach((tick) => {
          const x = margin.left;
          const y = margin.top + yScale(tick);

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x - 5, y);
          ctx.stroke();

          ctx.fillText(tick.toFixed(2), x - 8, y + 10);
        });

        // Draw data lines
        for (let i = 0; i < this.allDataNormalized.length; i++) {
          const { data, interpolatedIndexes } = this.allDataNormalized[i];
          const entry = this.markerEntries[i];
          const color = this.getTerritoryColor(entry.territory);
          const interpSet = new Set(interpolatedIndexes || []);

          ctx.strokeStyle = color;
          ctx.lineWidth = 5;

          for (let j = 1; j < data.length; j++) {
            const dashed = interpSet.has(j - 1) || interpSet.has(j);
            ctx.setLineDash(dashed ? [12, 8] : []);

            ctx.beginPath();
            ctx.moveTo(
              margin.left + xScale(j - 1),
              margin.top + yScale(data[j - 1]),
            );
            ctx.lineTo(margin.left + xScale(j), margin.top + yScale(data[j]));
            ctx.stroke();
          }

          ctx.setLineDash([]);
        }

        // Draw legend under X axis (same line, centered & space-evenly)
        if (this.markerEntries && this.markerEntries.length > 0) {
          const legendY = canvas.height - 25;
          const lineLength = 50;
          const gap = 12;
          const font = '32px Arial';
          ctx.font = font;

          const numEntries = this.markerEntries.length;
          const itemWidths = this.markerEntries.map((entry) => {
            const label = `${entry.territory}`;
            const textWidth = ctx.measureText(label).width;
            return lineLength + gap + textWidth;
          });

          const startX = margin.left;
          const endX = canvas.width - margin.right;
          const totalAvailableWidth = endX - startX;
          const totalItemsWidth = itemWidths.reduce((sum, w) => sum + w, 0);

          const spacingGap = (totalAvailableWidth - totalItemsWidth) / (numEntries + 1);
          let currentX = startX + spacingGap;

          this.markerEntries.forEach((entry, index) => {
            const color = this.getTerritoryColor(entry.territory);
            const label = `${entry.territory}`;
            const itemW = itemWidths[index];

            // Color bar indicator
            ctx.fillStyle = color;
            ctx.fillRect(currentX, legendY - 4, lineLength, 6);

            // Label text
            ctx.fillStyle = 'black';
            ctx.font = font;
            ctx.textAlign = 'left';
            ctx.fillText(label, currentX + lineLength + gap, legendY + 6);

            currentX += itemW + spacingGap;
          });
        }

        const imageURL = canvas.toDataURL('image/png');

        this.screenshotDataUrl = imageURL;
        this.screenshotDimensions = {
          width: canvas.width,
          height: canvas.height,
        };
        this.screenshotDialog = true;
      }
      catch (error) {
        console.error('Error taking simplified screenshot:', error);
      }
    },
    dragStart(event) {
      this.dragging = true;
      this.startDragX = event.clientX;
      this.startScrollerX = this.scrollerX;
      document.body.style.cursor = 'ew-resize';
    },
    drag(event) {
      if (!this.dragging) return;
      const dx = event.clientX - this.startDragX;
      this.scrollerX = Math.min(
        this.maxDrag,
        Math.max(0, this.startScrollerX + dx),
      );

      this.throttledSliderMove();
    },
    dragEnd() {
      this.dragging = false;
      document.body.style.cursor = 'default';
    },

    moveScale() {
      const domain = this.xScale.domain();
      if (domain.length > 0) {
        const totalWidth = this.width - this.margin.left - this.margin.right;
        const xGraph = Math.floor(
          (this.scrollerX / totalWidth) * this.totalWindow,
        );
        this.customDomain = [
          xGraph,
          Math.min(this.totalWindow, xGraph + domain[1] - domain[0]),
        ];
        this.drawChart();
      }
    },

    resetDomain() {
      this.customDomain = null;
      this.customYDomain = null;
      this.showBar = false;
      this.showMeasurement = false;
      this.drawChart();
    },

    drawChart() {
      if (this.checkValid()) {
        this.setAxis();
        if (this.innerWidth <= 0 || this.innerHeight <= 0) {
          return;
        }
        this.setupChart();
        this.drawData();

        this.updateGradient(d3.select(`#${this.gradientID}`));
      }
    },

    checkValid() {
      return this.markerEntries && this.markerEntries.length > 0;
    },

    sliceArrays(arrays, start, end) {
      return arrays.map(array => array.slice(start, end));
    },

    setAxis() {
      if (!this.markerEntries || this.markerEntries.length === 0) return;

      const allData = this.markerEntries.map((entry) => {
        const { data, interpolatedIndexes } = this.getData(
          entry.biomarker,
          entry.territory,
        );

        return { data, interpolatedIndexes };
      });
      this.allDataNormalized = allData;
      let start = 0;
      let end = this.allDataNormalized[0].data.length;
      this.totalWindow = this.allDataNormalized[0].data.length;

      if (this.customDomain) {
        [start, end] = this.customDomain;
      }

      let min;
      let max;

      for (let i = 0; i < this.allDataNormalized.length; i++) {
        const [minTemp, maxTemp] = this.findMinMax(
          this.allDataNormalized[i].data,
        );
        if (min === undefined || minTemp < min) min = minTemp;
        if (max === undefined || maxTemp > max) max = maxTemp;
      }

      const { margin } = this;
      this.innerWidth = this.width - margin.left - margin.right;
      this.innerHeight = this.height - margin.top - margin.bottom;

      this.xScale = d3
        .scaleLinear()
        .domain([start, end])
        .range([0, this.innerWidth]);

      this.yScale = d3
        .scaleLinear()
        .domain([min, max])
        .range([this.innerHeight, 0]);
    },

    drawPhaseLines(g, phaseIndexes, xScale, yScale, chartWidth) {
      const phaseColors = ['#FF5161', '#00fe9b'];
      const yDomain = yScale.domain();
      const leftThreshold = 0.1 * chartWidth;

      for (let i = 0; i < phaseIndexes.length; i++) {
        const { type, data } = phaseIndexes[i];
        for (let phaseNumber = 0; phaseNumber < data.length; phaseNumber++) {
          const xPosition = xScale(data[phaseNumber]);
          const textAnchor = xPosition < leftThreshold ? 'start' : 'start';
          const xOffset = xPosition < leftThreshold ? 12 : -3;

          this.drawPhaseLine(g, xPosition, yScale, yDomain, phaseColors[i]);
          this.drawPhaseLabel(
            g,
            type,
            xPosition,
            xOffset,
            yScale,
            yDomain,
            textAnchor,
            phaseNumber,
          );
        }
      }
    },

    drawPhaseLine(g, xPosition, yScale, yDomain, color) {
      g.append('line')
        .attr('x1', xPosition)
        .attr('y1', yScale(yDomain[0]))
        .attr('x2', xPosition)
        .attr('y2', yScale(yDomain[1]))
        .attr('stroke', color)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,8')
        .attr('opacity', 0.9)
        .attr('pointer-events', 'none');
    },

    drawPhaseLabel(
      g,
      label,
      xPosition,
      xOffset,
      yScale,
      yDomain,
      textAnchor,
      index,
    ) {
      const yPosition = yScale(yDomain[1]) + 15;
      const xPositionFinal = xPosition + xOffset;
      g.append('text')
        .attr('pointer-events', 'none')
        .attr('x', xPositionFinal)
        .attr('y', yPosition)
        .attr('fill', '#fefefe')
        .attr('text-anchor', textAnchor)
        .attr('font-size', '11px')
        .attr('transform', `rotate(-90, ${xPositionFinal}, ${yPosition})`)
        .text(label);
      if (label === 'ED') {
        g.append('text')
          .attr('x', xPosition - 2.5)
          .attr('y', this.innerHeight + 15)
          .attr('fill', '#fefefe')
          .attr('text-anchor', textAnchor)
          .attr('font-size', '11px')
          .text(index + 1);
      }
    },

    updateGradient(gradient) {
      gradient.selectAll('*').remove();
      const indexArray = this.getPhaseIndex();

      const edIndexes = indexArray.filter(index => index.type === 'ED')[0]
        .data;

      const xDomain = this.xScale.domain();
      const totalLength = xDomain[1] - xDomain[0];

      for (let i = 0; i < edIndexes.length - 1; i++) {
        const startOffset = Math.max(
          0,
          (edIndexes[i] - xDomain[0]) / totalLength,
        );
        const endOffset = Math.min(
          1,
          (edIndexes[i + 1] - xDomain[0]) / totalLength,
        );

        gradient
          .append('stop')
          .attr('offset', `${startOffset * 100}%`)
          .attr('stop-color', 'rgba(38, 151, 192, 0.35)')
          .attr('stop-opacity', 0.05);

        gradient
          .append('stop')
          .attr('offset', `${endOffset * 100}%`)
          .attr('stop-color', 'rgba(38, 151, 192, 0.35)')
          .attr('stop-opacity', 0.9);
      }
    },

    handleLegendHover(marker) {
      this.hoveredMarker = marker;
      this.updateCurveOpacities();
    },

    updateCurveOpacities() {
      if (!this.$refs.chart) return;
      const g = d3.select(this.$refs.chart).selectAll('svg').select('g');
      g.selectAll('.curve-path').each((d, i, nodes) => {
        const el = d3.select(nodes[i]);
        const territory = el.attr('data-territory');
        const isDimmed = this.hoveredMarker && this.hoveredMarker !== territory;
        el.transition().duration(150).attr('opacity', isDimmed ? 0.01 : 0.9);
      });
    },

    plotLine(data, g, line, color, dashed = false, territory = null) {
      const isDimmed = this.hoveredMarker && this.hoveredMarker !== territory;
      const initialOpacity = isDimmed ? 0.01 : 0.9;
      const path = g
        .append('path')
        .datum(data)
        .attr('class', 'curve-path')
        .attr('data-territory', territory)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 4)
        .attr('stroke-linecap', 'round')
        .attr('opacity', initialOpacity)
        .attr('d', line)
        .attr('pointer-events', 'none');

      if (dashed) {
        path.attr('stroke-dasharray', '4,8').attr('stroke-width', 3);
      }
    },

    buildSegments(data, interpolatedIndexes) {
      const interpSet = new Set(interpolatedIndexes || []);

      const segments = [];
      if (data.length < 2) return segments;

      let currentPoints = [{ x: 0, y: data[0] }];
      let currentDashed = interpSet.has(0);

      for (let i = 1; i < data.length; i++) {
        const currInterp = interpSet.has(i);
        const futureInterp = i < data.length - 1 ? interpSet.has(i + 1) : false;
        const segmentDashed = futureInterp || currInterp;

        if (segmentDashed === currentDashed) {
          currentPoints.push({ x: i, y: data[i] });
        }
        else {
          currentPoints.push({ x: i, y: data[i] });
          segments.push({ points: currentPoints, dashed: currentDashed });
          currentPoints = [{ x: i, y: data[i] }];
          currentDashed = segmentDashed;
        }
      }

      if (currentPoints.length > 1) {
        segments.push({ points: currentPoints, dashed: currentDashed });
      }

      return segments;
    },

    drawData() {
      const line = d3
        .line()
        .x(d => this.xScale(d.x))
        .y(d => this.yScale(d.y));

      const g = d3.select(this.$refs.chart).selectAll('svg').select('g');

      for (let i = 0; i < this.allDataNormalized.length; i++) {
        const { data, interpolatedIndexes } = this.allDataNormalized[i];
        const formattedData = this.formatData(data);
        const entry = this.markerEntries[i];
        const color = this.getTerritoryColor(entry.territory);
        const segments = this.buildSegments(data, interpolatedIndexes);

        if (segments.length === 0) {
          this.plotLine(formattedData, g, line, color, false, entry.territory);
        }
        else {
          segments.forEach((segment) => {
            this.plotLine(segment.points, g, line, color, segment.dashed, entry.territory);
          });
        }
      }

      const xDomain = this.xScale.domain();

      const chartWidth = this.xScale(xDomain[1]) - this.xScale(xDomain[0]);

      const indexArray = this.getPhaseIndex();

      this.drawPhaseLines(g, indexArray, this.xScale, this.yScale, chartWidth);

      this.drawImageIndexLine();
    },

    drawImageIndexLine() {
      if (!this.xScale || !this.yScale) return;

      const imageIndexValue = this.imageIndex;
      if (Number.isNaN(imageIndexValue)) return;

      const g = d3.select(this.$refs.chart).selectAll('svg').select('g');

      g.selectAll('.image-index-line').remove();

      const xPosition = this.xScale(imageIndexValue);
      const yDomain = this.yScale.domain();

      g.append('line')
        .attr('class', 'image-index-line')
        .attr('x1', xPosition)
        .attr('y1', this.yScale(yDomain[0]))
        .attr('x2', xPosition)
        .attr('y2', this.yScale(yDomain[1]))
        .attr('stroke', 'white')
        .attr('stroke-width', 3)
        .attr('stroke-dasharray', '5,5')
        .attr('opacity', 0.8)
        .attr('pointer-events', 'none');
    },

    setupChart() {
      d3.select(this.$refs.chart).selectAll('svg').remove();

      const { margin } = this;

      const svg = d3
        .select(this.$refs.chart)
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
        .on('click', event => this.handleGraphClick(event));

      const defs = svg.append('defs');

      const gradient = defs
        .append('linearGradient')
        .attr('id', this.gradientID);

      this.updateGradient(gradient);

      svg
        .append('defs')
        .append('svg:clipPath')
        .attr('id', 'clip')
        .append('svg:rect')
        .attr('width', this.innerWidth)
        .attr('height', this.innerHeight + margin.top)
        .attr('x', -5)
        .attr('y', -5);

      const g = svg
        .append('g')
        .attr('clip-path', 'url(#clip)')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      g.insert('rect', ':first-child')
        .attr('x', 0)
        .attr('y', -margin.top)
        .attr('width', this.innerWidth)
        .attr('height', this.innerHeight + margin.top)
        .style('fill', `url(#${this.gradientID})`);

      const brushed = (event) => {
        this.showMeasurement = false;
        if (!event || !event.selection) return;

        if (this.brushMode === 'zoom') {
          const extent = event.selection.map(this.xScale.invert);
          if (extent[1] - extent[0] >= 4) {
            extent[0] = Math.max(0, Math.floor(extent[0]) - 1);
            extent[1] = Math.floor(extent[1]) + 1;

            this.customDomain = extent;
            this.drawChart();
            this.showBar = true;

            const totalWidth
              = this.width - this.margin.left - this.margin.right;
            const width
              = ((extent[1] - extent[0]) / this.totalWindow) * totalWidth;
            this.scrollerWidth = width;
            this.scrollerX = (extent[0] / this.totalWindow) * totalWidth;
            this.maxDrag = totalWidth - width;
          }
          else {
            this.resetDomain();
          }
        }
        else if (this.brushMode === 'measure') {
          const [[x0, y0], [x1, y1]] = event.selection;

          const xVal0 = this.xScale.invert(x0);
          const xVal1 = this.xScale.invert(x1);
          const yVal0 = this.yScale.invert(y0);
          const yVal1 = this.yScale.invert(y1);

          this.deltaX = Math.abs(xVal1 - xVal0);
          this.deltaY = Math.abs(yVal1 - yVal0);

          this.measurementX = (x0 + x1) / 2 + this.margin.left;
          this.measurementY = (y0 + y1) / 2 + this.margin.top;
          this.showMeasurement = true;
        }
      };

      let brush;

      if (this.brushMode === 'measure') {
        brush = d3
          .brush()
          .extent([
            [0, 0],
            [this.innerWidth, this.innerHeight],
          ])
          .on('end', brushed);
      }
      else {
        brush = d3
          .brushX()
          .extent([
            [0, 0],
            [this.innerWidth, this.innerHeight],
          ])
          .on('end', brushed);
      }

      g.append('g').attr('class', 'brush').call(brush);

      // svg
      //   .append('text')
      //   .attr('x', -margin.top - this.innerHeight / 2)
      //   .attr('y', 9)
      //   .attr('font-size', '12px')
      //   .attr('fill', 'white')
      //   .attr('text-anchor', 'middle')
      //   .attr('transform', 'rotate(-90)')
      //   .text(this.yAxisLabel);

      svg
        .append('g')
        .attr('transform', `translate(${margin.left - 2},${margin.top})`)
        .call(d3.axisLeft(this.yScale).ticks(7).tickSize(0))
        .selectAll('text')
        .attr('font-size', '12px');

      svg.selectAll('.domain').style('display', 'none');
    },
  },
};
</script>

<template>
  <div
    style="
      position: relative;
      padding-bottom: 10px;
      padding-top: 8px;
      width: 100%;
    "
    @mousemove="drag"
    @mouseup="dragEnd"
    @mouseleave="dragEnd"
    @dblclick="resetDomain"
  >
    <div
      ref="chart"
      style="position: relative"
      @mousemove="handleMouseMove"
      @mouseleave="hideTooltip"
    >
      <AnalysisGraphLegend
        :is-visible="showLegend"
        :markers="legendEntries"
        :colors="legendColors"
        @hover-label="handleLegendHover"
      />
      <div
        v-show="showTooltip"
        class="tooltip"
        :style="{ left: `${tooltipX}px`, top: `${tooltipY}px` }"
      >
        X: {{ formatCoordinate(currentX, 0) }}, Y:
        {{ formatCoordinate(currentY) }}
      </div>
      <div
        v-show="showTooltip"
        class="tooltip-indicator"
        :style="{
          left: `${snappedPointX}px`,
          top: `${snappedPointY}px`,
          borderColor: snappedPointColor,
        }"
      />
      <div
        v-if="showMeasurement"
        class="measurement"
        :style="{ left: `${measurementX}px`, top: `${measurementY}px` }"
      >
        <div>ΔX: {{ formatCoordinate(deltaX, 2) }}</div>
        <div>ΔY: {{ formatCoordinate(deltaY, 2) }}</div>
      </div>
    </div>
    <div
      v-if="showBar"
      class="scroll-bar"
      :style="{ width: `${width - margin.left - margin.right}px` }"
      :class="{ expand: dragging }"
    >
      <div
        class="scroller"
        :style="{ width: `${scrollerWidth}px`, marginLeft: `${scrollerX}px` }"
        :class="{ expand: dragging }"
        @mousedown="dragStart"
        @mousemove="drag"
        @mouseup="dragEnd"
      />
    </div>
    <div class="brush-mode-controls">
      <button
        :class="{ active: brushMode === 'measure' }"
        title="Measure distances"
        @click="setBrushMode('measure')"
      >
        📏
      </button>
      <button
        :class="{ active: brushMode === 'zoom' }"
        title="Zoom"
        @click="setBrushMode('zoom')"
      >
        🔍
      </button>
      <button title="Take Screenshot" @click="takeScreenshot">
        📸
      </button>
    </div>

    <ScreenshotDialog
      :is-open="screenshotDialog"
      :image-info="{
        imageDataURL: screenshotDataUrl,
        title: `OCG Curves slice ${viewerIndx.sliceIdx + 1}`,
        viewerID,
        type: 'curves',
        curveType: Object.keys(targetMarkers)[0],
        imageIndices: viewerIndx,
      }"
      @update:dialog-open="screenshotDialog = $event"
      @save="handleScreenshotSave"
      @discard="handleScreenshotDiscard"
    />
  </div>
</template>

<style scoped>
.domain {
  display: none;
}

.scroll-bar {
  position: absolute;
  height: 6px;
  border-radius: 4px;
  /* background-color: #ddd; */
  background-color: rgba(85, 85, 85, 0.3);
  border: 1px solid #dddddd85;
  display: flex;
  align-items: center;
  z-index: 999;
  bottom: 8px;
  left: 45px;
  transition: height 0.2s ease;
}

.scroll-bar:hover {
  height: 10px;
  transition: height 0.15s ease;
}

.scroller:hover {
  height: 10px;
  transition: height 0.15s ease;
}

.scroller {
  height: 6px;
  border-radius: 4px;
  background-color: #dcdcdc;
  width: 40px;
  margin-left: 50px;
  cursor: ew-resize;
  transition: height 0.2s ease;
}

.expand {
  height: 10px;
}

.tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px;
  border-radius: 3px;
  pointer-events: none;
  font-size: 12px;
}

.tooltip-indicator {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #101010;
  border: 2px solid #ff6700;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  z-index: 101;
}

.measurement {
  position: absolute;
  background-color: rgba(66, 66, 66, 0.85);
  color: white;
  padding: 8px 10px;
  border-radius: 4px;
  pointer-events: none;
  font-size: 12px;
  font-weight: bold;
  transform: translate(-50%, -50%);
  z-index: 100;
  border: 1px solid #999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.measurement div {
  margin: 2px 0;
}

.measurement div:first-child {
  color: #3498db;
}

.measurement div:last-child {
  color: #e74c3c;
}

.brush-mode-controls {
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  gap: 5px;
  align-items: center;
}

.brush-mode-controls button {
  border-radius: 3px;
  color: white;
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  padding: 0;
}

.brush-mode-controls button.active {
  background-color: #3498db;
  border-color: white;
}

.smooth-curve-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  color: white;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
}

.smooth-curve-toggle input {
  margin: 0;
}
</style>
